import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getLocations } from '../../shared/repos/graphql/locations';
import { setCaptchaToken } from '../../shared/utilities/captcha';
import captchaAction from '../../shared/constants/captchaAction';
import * as elementsActions from '../../redux/actions/elements';
import ReCAPTCHAForm from '../../components/ReCaptcha/ReCaptcha';
import elementConstants from '../../shared/constants/element';

const EventAddress = ({ value, onSelect, invalid }) => {
  const [text, setText] = useState(value);
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState('');
  const [challenge, setChallenge] = useState(false);
  const [retry, setRetry] = useState(0);
  const sequence = useRef(0);
  const verified = useRef(false);
  const selected = useRef(value);
  const dispatch = useDispatch();
  const useRecaptcha = useSelector(state => state.elements.useRecaptcha);

  useEffect(() => {
    const id = sequence.current;
    if (text.length < 3 || text === selected.current) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setMessage('Searching addresses…');
      try {
        if (useRecaptcha === 1 && !verified.current) {
          await setCaptchaToken(
            token => dispatch(elementsActions.setVerificationToken(token)),
            action => dispatch(elementsActions.setVerificationAction(action)),
            captchaAction.addressSearching,
          );
        }
        const response = await getLocations(text, null, null, true, false, null);
        if (!active || id !== sequence.current) return;
        dispatch(elementsActions.setVerificationProvider(''));
        verified.current = false;
        setResults(response.data.locationSearch || []);
        setMessage(response.data.locationSearch?.length ? 'Select an address below.' : 'No addresses found. Try a more complete address.');
      } catch (error) {
        if (!active || id !== sequence.current) return;
        setChallenge(String(error.message).includes('401'));
        setMessage('Address search is unavailable. Please try again.');
      }
    }, 500);
    return () => { active = false; clearTimeout(timer); };
  }, [text, retry, dispatch, useRecaptcha]);

  const edit = event => {
    sequence.current += 1;
    selected.current = '';
    setText(event.target.value);
    setResults([]);
    setMessage('');
    onSelect('', null, null);
  };
  const choose = result => {
    sequence.current += 1;
    const label = [result.addressDetails?.address1 || result.address1, result.addressDetails?.city,
      result.addressDetails?.state, result.addressDetails?.postcode].filter(Boolean).join(', ');
    const rawLat = result.addressDetails?.lat ?? result.lat;
    const rawLng = result.addressDetails?.lng ?? result.lng;
    const lat = rawLat === null || rawLat === undefined || rawLat === '' ? NaN : Number(rawLat);
    const lng = rawLng === null || rawLng === undefined || rawLng === '' ? NaN : Number(rawLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setMessage('This address could not be located. Please select another address.');
      onSelect('', null, null);
      return;
    }
    selected.current = label;
    setText(label);
    setResults([]);
    setMessage('Address selected.');
    onSelect(label, lat, lng);
  };
  const verify = token => {
    if (!token) return;
    dispatch(elementsActions.setVerificationToken(token));
    dispatch(elementsActions.setVerificationProvider(elementConstants.challengeRecaptcha));
    verified.current = true;
    setChallenge(false);
    setRetry(current => current + 1);
  };
  return (
    <div>
      <label htmlFor="donation-event-address">
        Event location
        <input
          id="donation-event-address"
          value={text}
          onChange={edit}
          aria-invalid={invalid}
          aria-describedby={invalid ? 'donation-address-status donation-event_location-error' : 'donation-address-status'}
          autoComplete="off"
        />
      </label>
      <p id="donation-address-status" role="status">{message}</p>
      {results.length > 0 && (
        <ul aria-label="Event address suggestions">
          {results.map((result, index) => (
            <li key={result.id || result.externalId || index}>
              <button type="button" onClick={() => choose(result)}>
                {[result.address1, result.addressDetails?.city, result.addressDetails?.state].filter(Boolean).join(', ')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {challenge && <ReCAPTCHAForm size="compact" setToken={verify} />}
      {message.includes('unavailable') && <button type="button" onClick={() => setRetry(current => current + 1)}>Retry address search</button>}
    </div>
  );
};

EventAddress.propTypes = {
  value: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
};
export default EventAddress;
